/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '../types/entities';
import AbstractWorker, {AbstractWorkerConfig} from './AbstractWorker';
import {
  NotificationMessage,
  NotificationsWorkerContext
} from './types';

/**
 * Defines the options to be used when starting the NotificationsWorker.
 */
export interface NotificationConfig extends AbstractWorkerConfig {
  config: GenericObject;
  account: GenericObject;
  autoTerminate?: boolean;
};

/**
 * The Notifications Worker provides the single point of access for performing short
 * polling of alert notifications on a specified interval. The async polling request,
 * validation, and results verification is off-loaded to a dedicated Web Worker on
 * a separate thread, thus freeing up resources on the main UI thread, which is only
 * responsible for handling updates in context of the application.
 *
 * **IMPORTANT**: While the `NotificationsWorker` class is explicitly exported, client
 * implementations should **ALWAYS** use the `default export` which essentially ensures
 * only a single `NotificationsWorker` instance will ever be created.
 *
 * ```typescript
 *  // start the worker ...
 *  NotificationsWorker.start({
 *    account  : getState().globalGroup,
 *    onUpdate : notifications => {
 *      dispatch({
 *        type                       : ALERTS_REFRESH_UPDATED,
 *        latest                     : notifications,
 *        urgentNotificationsEnabled : getUrgentNotificationsEnabled()
 *      });
 *    },
 *    config
 *  });
 *
 *  // notify of the worker of a root account change (global group) ...
 *  NotificationsWorker.onAccountChange(globalGroup);
 * ```
 */
export class NotificationsWorker extends AbstractWorker {
  /**
   * The start method is to be invoked when a client wishes to initialize short
   * polling of Alert Notifications. This method requires the current global
   * group for which the alerts are to be retrieved, a function to be invoked
   * when updates are received, and the ConnectUtils configuration from which
   * the calls are to be configured.
   *
   * ```typescript
   *  if (!NotificationsWorker.isActive) {
   *    NotificationsWorker.start({
   *      account  : getState().globalGroup,
   *      onUpdate : notifications => {
   *        dispatch({
   *          type                       : ALERTS_REFRESH_UPDATED,
   *          latest                     : notifications,
   *          urgentNotificationsEnabled : getUrgentNotificationsEnabled()
   *        });
   *      },
   *      config
   *    });
   *  }
   * ```
   */
  start = ({account, onUpdate, onUpdateFailure, onError, config, autoTerminate = false}: NotificationConfig) => {
    if (!this.isActive || autoTerminate) {
      autoTerminate && this.destruct();
      const worker = new Worker(new URL('./notifications.worker.ts', import.meta.url));
      this.initialize(worker, {
        context : NotificationsWorkerContext,
        onUpdateFailure,
        onUpdate,
        onError
      });
      this.send(NotificationMessage.INITIALIZE, account, config);
    }
  }

  /**
   * The onGlobalGroupChange method is to be invoked when the current global group
   * (account) has changed, at which point the newly selected group will be used
   * for all subsequent Alert Notification polling.
   *
   * ```typescript
   *  // notify of the worker of a root account change (global group) ...
   *  NotificationsWorker.onAccountChange(globalGroup);
   * ```
   */
  onAccountChange(account: GenericObject) {
    this.send(NotificationMessage.ACCOUNT_CHANGE, account);
  }
}

// exports a singleton instance of the private notification worker
export default new NotificationsWorker();
